<template>
    <div>
        <!-- Header with title, count, and sort dropdown -->
        <div class="tc-header">
            <div class="tc-header__title">
                <i class="pi pi-comments"></i>
                {{ allComments.length }} {{ allComments.length === 1 ? 'Comment' : 'Comments' }}
            </div>
            <div v-if="allComments.length > 1" class="tc-header__sort">
                <Button
                    :icon="sortOrder === 'newest' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up-alt'"
                    text
                    rounded
                    size="small"
                    @click="toggleSort"
                    aria-label="Toggle sort order"
                />
            </div>
        </div>

        <!-- Comments container -->
        <div ref="scrollContainer" class="tc-container">
            <div v-if="loading" class="tc-loader">
                <task-comments-loader />
            </div>

            <div v-else-if="sortedComments.length" class="tc-list">
                <div
                    v-for="(item, id) in sortedComments"
                    :key="id"
                    class="tc-card"
                    :class="{ 'tc-card--system': ['archive', 'unarchive'].includes(item.type) }"
                >
                    <!-- Card header -->
                    <div class="tc-card__header">
                        <div class="tc-card__meta">
                            <span class="tc-card__author">
                                <i class="pi pi-user" style="font-size: 0.75rem; margin-right: 0.25rem; color: #94a3b8;"></i>
                                {{ item.author_name }}
                            </span>
                            <span class="tc-card__date">{{ item.created_at }}</span>

                            <span v-if="item.created_at !== item.updated_at && !['archive', 'unarchive'].includes(item.type)"
                                  v-tooltip="'Last edited: ' + item.updated_at"
                                  class="tc-card__badge tc-card__badge--edited">
                                Edited
                            </span>
                            <span v-if="['archive'].includes(item.type)"
                                  v-tooltip="item.updated_at"
                                  class="tc-card__badge tc-card__badge--archived">
                                Archived
                            </span>
                            <span v-else-if="['unarchive'].includes(item.type)"
                                  v-tooltip="item.updated_at"
                                  class="tc-card__badge tc-card__badge--unarchived">
                                Unarchived
                            </span>
                        </div>

                        <div v-if="!['archive', 'unarchive'].includes(item.type)" class="tc-card__actions">
                            <Button v-if="item.editable"
                                    icon="pi pi-pencil"
                                    @click="editComment(item)"
                                    text
                                    rounded
                                    aria-label="Edit"
                                    v-tooltip="'Edit comment'" />
                            <Button v-if="item.deletable"
                                    icon="pi pi-trash"
                                    @click="confirmRequest(item, deleteComment)"
                                    severity="danger"
                                    text
                                    rounded
                                    aria-label="Delete"
                                    v-tooltip="'Delete comment'" />
                        </div>
                    </div>

                    <!-- Card body -->
                    <div class="tc-card__body">
                        <span v-html="item.comment" />
                    </div>

                    <!-- Attachments -->
                    <div v-if="item.attachment?.length" class="tc-card__attachments">
                        <file-attachments :attachments="item.attachments" />
                    </div>
                </div>
            </div>

            <div v-else class="tc-empty">
                <i class="pi pi-comments"></i>
                <span class="tc-empty__text">No comments yet</span>
                <span>Be the first to add a comment</span>
            </div>
        </div>
    </div>

    <!-- Edit comment dialog -->
    <Dialog v-model:visible="editCommentDialog"
            :style="{ width: '600px' }"
            header="Edit comment"
            :modal="true"
            class="p-fluid tc-edit-dialog">
        <base-editor v-model="editCommentData.comment" />

        <DataTable :value="editCommentData.files">
            <Column field="actions" header="FILE" style="min-width: 8rem">
                <template #body="slotProps">
                    <a href="#" @click.prevent="downloadFile(slotProps.data.file)">{{ slotProps.data.file.original_name }}</a>
                </template>
            </Column>
            <Column field="actions" header="ACTION" style="min-width: 8rem">
                <template #body="slotProps">
                    <Button
                        icon="pi pi-trash"
                        class="p-button-rounded p-button-danger"
                        @click="confirmRequest(slotProps.data, removeCommentAttachment)"
                        :loading="editCommentSaving"
                    />
                </template>
            </Column>
        </DataTable>

        <template #footer>
            <Button label="Cancel" icon="pi pi-times" severity="warning" @click="editCommentDialog = false" :loading="editCommentSaving" />
            <Button class="ml-2 p-button-primary" label="Submit" icon="pi pi-check" @click="onSubmitCommentEdit" :loading="editCommentSaving" />
        </template>
    </Dialog>
</template>

<script setup>
import TaskCommentsLoader from '../../skeleton/TaskCommentsLoader.vue';
import FileAttachments from '../../../../../modules/file/components/FileAttachments.vue';
import { ref, computed, defineProps, watch, onMounted, nextTick, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';

import { userStore, commentStore, authStore, fileStore } from '../../../../../store/global-stores';
import { formatCommentDate } from '../../../../../utilities/date-formatter';
import { authApi } from '@/utilities/axios';
import { useConfirmRemove } from '@/composables/ConfirmComposable';
import { showToast } from '@/utilities/toast-handler';
const { confirmRequest } = useConfirmRemove();

// props
const props = defineProps({
    taskId: {
        type: [Number, String],
    },
});

const { users } = storeToRefs(userStore);
const { comments } = storeToRefs(commentStore);

// references
const loading = ref(false);
const scrollContainer = ref(null);

const editCommentDialog = ref(false)
const editCommentData = ref('')
const editCommentId = ref(-1)
const editCommentSaving = ref(false)

// Sort state
const sortOrder = ref('newest');

function toggleSort() {
    sortOrder.value = sortOrder.value === 'newest' ? 'oldest' : 'newest';
    scrollToBottom();
}

// computed properties
const allComments = computed(() => {
    return comments.value?.map((comment) => {
        return {
            ...comment,
            author_name: users.value?.find((user) => user.id === comment.author_id)?.fullname ?? 'System Admin',
            created_at: formatCommentDate(comment.created_at),
            updated_at: formatCommentDate(comment.updated_at),
            attachments: comment.attachment?.map((item) => item.file),
        };
    });
});

// sorted comments based on sortOrder
const sortedComments = computed(() => {
    const list = [...(allComments.value ?? [])];
    if (sortOrder.value === 'newest') {
        // newest first — sort by id descending (assuming higher id = newer)
        return list.sort((a, b) => b.id - a.id);
    } else {
        // oldest first — sort by id ascending
        return list.sort((a, b) => a.id - b.id);
    }
});


// watch for comment count changes only — scroll when new comment arrives
watch(
    () => allComments.value?.length ?? 0,
    (newLen, oldLen) => {
        // only auto-scroll when comments are added (not on edits)
        if (newLen > oldLen && sortOrder.value === 'newest') {
            scrollToBottom();
        }
    },
);

const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

onMounted(async () => {
    loading.value = true;
    await loadUsers();
    await loadTaskComments();
});

onUnmounted(() => {
    commentStore.initComments();
});

function editComment(commentData) {
    editCommentDialog.value = true
    editCommentData.value = commentData

    /*
        Keeping the attachment list a different and separate list: the attachment's table will render that.
        This list will be modified if the User decides to remove an attachment.
        The popup will use `files` property so the task comments will not be affected until the changes are recorded.
     */
    editCommentData.value.files = commentData.attachment

    editCommentId.value = commentData.id
}

function onSubmitCommentEdit() {
    loading.value = true;
    editCommentSaving.value = true;

    let attachments = []

    if (editCommentData.value.files && Array.isArray(editCommentData.value.files)) {
        editCommentData.value.files.forEach(link => {
            attachments.push(link.file.id)
        })
    }

    let bodyParams = {
        company_id: company_id.value,
        comment: editCommentData.value.comment,
        attachments: attachments
    }

    authApi.patch(`/v2/tasks/${props.taskId}/comments/${editCommentId.value}`, bodyParams)
        .then(() => {
            loadTaskComments().then(() => {
                loading.value = false;
                editCommentSaving.value = false;
                editCommentDialog.value = false
            })
        })
}

function deleteComment(commentId) {
    loading.value = true;
    authApi.delete(`/v2/tasks/${props.taskId}/comments/${commentId}?companyId=${company_id.value}`)
        .then(() => {
            loadTaskComments().then(() => {
                loading.value = false;
            })
        });
}

// load all existing users from the company
const loadUsers = async () => {
    // no params and allData flag is set true
    await userStore.fetchAllUsers({}, true);
};

const loadTaskComments = async () => {
    await commentStore.getTaskComments(props.taskId);
    loading.value = false;
    await scrollToBottom();
};

const scrollToBottom = async () => {
    await nextTick();
    const container = scrollContainer.value;
    container?.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
    });
};


const downloadFile = async (file) => {
    try {
        loading.value = true;
        await fileStore.downloadFile(file.id, file.original_name);
    } catch (error) {
        await showToast(error.response, 'Unable to download the file');
    } finally {
        loading.value = false;
    }
};

function removeCommentAttachment(linkId) {
    editCommentData.value.files = editCommentData.value.files.filter(function( obj ) {
        return obj.id !== linkId;
    });
}

</script>

<style lang="scss">
@import '../assets/task-comments.scss';
</style>
